/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Settings V2
 *
 * User settings page for image generation preferences and UI customization.
 * Built with Tailwind CSS v4.1 and V2 components.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Settings as SettingsIcon,
  Sliders,
  Wrench,
  FileImage,
  Monitor,
  Save,
  RotateCcw,
  Info,
  Sun,
  Moon,
} from 'lucide-react';
import {
  getUserSettings,
  updateUserSettings,
  getAvailablePresets,
  getSettingsOptions,
  settingsQueryKeys,
} from '@/lib/api/settings';
import type {
  UserSettings,
  UserSettingsUpdate,
  PresetOption,
} from '@/types/settings';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Input,
  Select,
  Switch,
  Button,
  Alert,
  AlertDescription,
  Loading,
} from '@/components/v2/ui';

export function SettingsV2() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();

  // Form State
  const [formData, setFormData] = useState<UserSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Queries
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: settingsQueryKeys.user(),
    queryFn: getUserSettings,
  });

  const { data: presets, isLoading: presetsLoading } = useQuery({
    queryKey: settingsQueryKeys.presets(),
    queryFn: getAvailablePresets,
  });

  const { data: options, isLoading: optionsLoading } = useQuery({
    queryKey: settingsQueryKeys.options(),
    queryFn: getSettingsOptions,
  });

  // Initialize form data when settings are loaded
  useEffect(() => {
    if (settings && !formData) {
      setFormData(settings);
    }
  }, [settings, formData]);

  // Mutation
  const updateMutation = useMutation({
    mutationFn: (data: UserSettingsUpdate) => updateUserSettings(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(t('toast.success.settingsSavedSuccess'));
        queryClient.invalidateQueries({ queryKey: settingsQueryKeys.user() });
        setHasChanges(false);
      } else {
        toast.error(response.error || t('errors.saving'));
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t('errors.saving'));
    },
  });

  // Handlers
  const handleChange = (field: keyof UserSettings, value: string | boolean) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [field]: value,
    });
    setHasChanges(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    updateMutation.mutate(formData);
  };

  const handleReset = () => {
    if (settings) {
      setFormData(settings);
      setHasChanges(false);
    }
  };

  // Loading State
  const isLoading = settingsLoading || presetsLoading || optionsLoading;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Loading size="lg" label={t('common.loading')} centered />
      </div>
    );
  }

  if (!formData || !options) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="error">
          <AlertDescription>
            {t('errors.loading')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <SettingsIcon className="h-8 w-8 text-neutral-900 dark:text-neutral-50" />
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
              {t('common.settings')}
            </h1>
          </div>
          <p className="text-neutral-600 dark:text-neutral-400">
            Passen Sie Ihre Standard-Einstellungen für die Bildgenerierung an
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Standard-Einstellungen */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Sliders className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                <CardTitle>Standard-Einstellungen</CardTitle>
              </div>
              <CardDescription>
                Grundeinstellungen für die Bildgenerierung
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-6 md:grid-cols-3">
                {/* Seitenverhältnis */}
                <div className="space-y-2">
                  <label htmlFor="ratio" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Standard-Seitenverhältnis
                  </label>
                  <Select
                    value={formData.default_ratio}
                    onValueChange={(value) => handleChange('default_ratio', value)}
                    options={options.ratios}
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Wird beim Erstellen eines neuen Bildes vorausgewählt.
                  </p>
                </div>

                {/* Preset */}
                <div className="space-y-2">
                  <label htmlFor="preset" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Standard-Preset
                  </label>
                  <Select
                    value={formData.default_preset}
                    onValueChange={(value) => handleChange('default_preset', value)}
                    options={
                      presets?.map((preset: PresetOption) => ({
                        value: preset.id,
                        label: `${preset.name}${preset.type === 'custom' ? ' (Custom)' : ''}`,
                      })) || []
                    }
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Wird beim Erstellen eines neuen Bildes vorausgewählt.
                  </p>
                </div>

                {/* Zustellungsmethode */}
                <div className="space-y-2">
                  <label htmlFor="delivery" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Standard-Zustellungsmethode
                  </label>
                  <Select
                    value={formData.default_delivery_method}
                    onValueChange={(value) => handleChange('default_delivery_method', value as 'wait' | 'mail')}
                    options={options.delivery_methods}
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    <strong>Warten:</strong> Bild direkt anzeigen. <strong>E-Mail:</strong> Innerhalb von 5 Minuten.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Experteneinstellungen */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                <CardTitle>Experteneinstellungen</CardTitle>
              </div>
              <CardDescription>
                Erweiterte Optionen für Format und Dateinamen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-6 md:grid-cols-3">
                {/* Bildformat */}
                <div className="space-y-2">
                  <label htmlFor="format" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Standard-Bildformat
                  </label>
                  <Select
                    value={formData.default_image_format}
                    onValueChange={(value) => handleChange('default_image_format', value)}
                    options={options.image_formats}
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    PNG: Verlustfrei. JPEG: Komprimiert. WebP/AVIF: Modern.
                  </p>
                </div>

                {/* Prefix */}
                <div className="space-y-2">
                  <label htmlFor="prefix" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Dateiname-Prefix
                  </label>
                  <Input
                    id="prefix"
                    type="text"
                    value={formData.default_filename_prefix}
                    onChange={(e) => handleChange('default_filename_prefix', e.target.value)}
                    placeholder="generated_"
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Beispiel: <code className="text-xs bg-neutral-100 dark:bg-neutral-800 px-1 py-0.5 rounded">product_abc-123.png</code>
                  </p>
                </div>

                {/* Suffix */}
                <div className="space-y-2">
                  <label htmlFor="suffix" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Dateiname-Suffix
                  </label>
                  <Input
                    id="suffix"
                    type="text"
                    value={formData.default_filename_suffix}
                    onChange={(e) => handleChange('default_filename_suffix', e.target.value)}
                    placeholder="_final, _v1"
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Beispiel: <code className="text-xs bg-neutral-100 dark:bg-neutral-800 px-1 py-0.5 rounded">abc-123_v1.png</code>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PNG-Einstellungen */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileImage className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                <CardTitle>PNG-Einstellungen</CardTitle>
              </div>
              <CardDescription>
                Hintergrund und Qualität für PNG-Bilder
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-6 md:grid-cols-2">
                {/* PNG Hintergrund */}
                <div className="space-y-2">
                  <label htmlFor="png_bg" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Hintergrundfarbe (PNG)
                  </label>
                  <Select
                    value={formData.default_png_background}
                    onValueChange={(value) => handleChange('default_png_background', value)}
                    options={options.png_backgrounds}
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    <strong>Weiß:</strong> Standard. <strong>Transparent:</strong> AI-entfernt Hintergrund.
                  </p>
                </div>

                {/* PNG Qualität */}
                <div className="space-y-2">
                  <label htmlFor="png_quality" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Bildqualität (PNG)
                  </label>
                  <Select
                    value={formData.default_png_quality}
                    onValueChange={(value) => handleChange('default_png_quality', value)}
                    options={options.png_qualities}
                  />
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    <strong>Web optimiert</strong> liefert die beste Balance.
                  </p>
                </div>
              </div>

              <Alert variant="info">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Web optimiert</strong> liefert die beste Balance aus Bildqualität und Ladegeschwindigkeit.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* UI-Einstellungen */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                <CardTitle>Oberfläche</CardTitle>
              </div>
              <CardDescription>
                Anpassungen der Benutzeroberfläche
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Selector */}
              <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center gap-2 mb-2">
                  {theme === 'light' ? (
                    <Sun className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  ) : (
                    <Moon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  )}
                  <label htmlFor="theme" className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Farbschema
                  </label>
                </div>
                <Select
                  value={theme}
                  onValueChange={(value) => setTheme(value as 'light' | 'dark')}
                  options={[
                    { value: 'light', label: 'Hell' },
                    { value: 'dark', label: 'Dunkel' }
                  ]}
                />
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                  Wählen Sie zwischen hellem und dunklem Farbschema für die Benutzeroberfläche.
                </p>
              </div>

              {/* Konfetti Toggle */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <label
                    htmlFor="confetti"
                    className="text-sm font-medium text-neutral-900 dark:text-neutral-100 cursor-pointer block mb-1"
                  >
                    Konfetti-Animation bei erfolgreicher Bildgenerierung
                  </label>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Zeigt eine Konfetti-Animation, wenn ein Bild erfolgreich generiert wurde. 🎉
                  </p>
                </div>
                <Switch
                  id="confetti"
                  checked={formData.confetti_enabled}
                  onCheckedChange={(checked) => handleChange('confetti_enabled', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Info-Hinweis */}
          <Alert variant="default">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Hinweis:</strong> Diese Einstellungen gelten nur für Sie und werden beim Erstellen eines neuen
              Bildes automatisch vorausgewählt. Sie können die Werte jederzeit ändern.
            </AlertDescription>
          </Alert>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <Button
              type="submit"
              variant="primary"
              disabled={!hasChanges || updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loading size="sm" className="mr-2" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Einstellungen speichern
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={!hasChanges || updateMutation.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('common.reset')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
